/**
 * Pure helpers for the Feature flags page: how a stored flag maps to the
 * drawer's three modes (Nobody / Types of user + named people / Everyone) and
 * back to the payload PUT /api/v1/feature-flags/{key} accepts.
 *
 * Kept free of React and MUI so the mapping is easy to check on its own.
 */

import type { FeatureFlag, FeatureFlagAudience, FeatureFlagInput } from '../services/my-guides-admin-api';

export type FlagMode = 'nobody' | 'types' | 'everyone';

/** The audiences that can be ticked in "Types of user" (everyone has its own mode). */
export type TypeAudience = Exclude<FeatureFlagAudience, 'everyone'>;

export const TYPE_AUDIENCES: { value: TypeAudience; label: string; hint?: string }[] = [
  { value: 'staff', label: 'Tripsby staff', hint: 'Staff and admins' },
  { value: 'admin', label: 'Tripsby admins', hint: 'Already included in Tripsby staff' },
  { value: 'business', label: 'Business users' },
  { value: 'traveller', label: 'Travellers' },
];

/** Same shape the backend enforces: dotted lower-case parts such as my_guides.plan_trip. */
const FLAG_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}(\.[a-z][a-z0-9_]{0,39}){0,3}$/;

export function isValidFlagKey(key: string): boolean {
  return FLAG_KEY_PATTERN.test(key);
}

/**
 * Keys the server derives for each person (BUILD_CONTRACT 8.1: my_guides.create is
 * true only when the audience rule passes). They are never stored, so the backend
 * refuses to create or edit them; the dialog says so before the request is sent.
 */
export const RESERVED_FLAG_KEYS: readonly string[] = ['my_guides.create'];

export function isReservedFlagKey(key: string): boolean {
  return RESERVED_FLAG_KEYS.includes(key);
}

export interface FlagDraft {
  mode: FlagMode;
  typeAudiences: TypeAudience[];
  users: string[];
  blockedUsers: string[];
  description: string;
}

export function modeOf(flag: Pick<FeatureFlag, 'enabled' | 'audiences'>): FlagMode {
  if (!flag.enabled) return 'nobody';
  if (flag.audiences.includes('everyone')) return 'everyone';
  return 'types';
}

export function draftFromFlag(flag: FeatureFlag): FlagDraft {
  return {
    mode: modeOf(flag),
    typeAudiences: flag.audiences.filter((a): a is TypeAudience => a !== 'everyone'),
    users: [...flag.users],
    blockedUsers: [...flag.blockedUsers],
    description: flag.description,
  };
}

/**
 * The PUT body for a draft. "Nobody" only switches the flag off and keeps the
 * ticked types and named people, so turning it back on restores them.
 */
export function payloadFromDraft(draft: FlagDraft): Required<FeatureFlagInput> {
  return {
    enabled: draft.mode !== 'nobody',
    description: draft.description.trim(),
    audiences: draft.mode === 'everyone' ? ['everyone'] : [...draft.typeAudiences],
    users: [...draft.users],
    blockedUsers: [...draft.blockedUsers],
  };
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().every((value, i) => value === [...b].sort()[i]);

/** True when saving the draft would change nothing. */
export function isDraftUnchanged(flag: FeatureFlag, draft: FlagDraft): boolean {
  const next = payloadFromDraft(draft);
  return (
    next.enabled === flag.enabled &&
    next.description === flag.description.trim() &&
    sameList(next.audiences, flag.audiences) &&
    sameList(next.users, flag.users) &&
    sameList(next.blockedUsers, flag.blockedUsers)
  );
}

export type ChipKind = 'off' | 'everyone' | 'staff' | 'type' | 'people' | 'blocked';

export interface FlagChip {
  label: string;
  kind: ChipKind;
}

const AUDIENCE_LABELS: Record<FeatureFlagAudience, string> = {
  staff: 'Staff',
  admin: 'Admins',
  business: 'Business',
  traveller: 'Travellers',
  everyone: 'Everyone',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The "Who has it" chips for one table row. */
export function chipsForFlag(flag: FeatureFlag): FlagChip[] {
  if (!flag.enabled) return [{ label: 'Off', kind: 'off' }];

  const chips: FlagChip[] = [];
  if (flag.audiences.includes('everyone')) {
    chips.push({ label: 'Everyone', kind: 'everyone' });
  } else {
    for (const audience of flag.audiences) {
      chips.push({ label: AUDIENCE_LABELS[audience], kind: audience === 'staff' ? 'staff' : 'type' });
    }
    if (chips.length === 0 && flag.users.length === 0) {
      chips.push({ label: 'Nobody', kind: 'off' });
    }
    if (flag.users.length > 0) {
      chips.push({ label: `+${plural(flag.users.length, 'person', 'people')}`, kind: 'people' });
    }
  }
  if (flag.blockedUsers.length > 0) {
    chips.push({ label: `${flag.blockedUsers.length} blocked`, kind: 'blocked' });
  }
  return chips;
}

/** "Jonathan McClure" style label from an email's local part, for the Updated column. */
export function nameFromEmail(email?: string | null): string {
  if (!email) return '';
  const local = email.split('@')[0] || email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
